import { Autocomplete, Box, Button, CircularProgress, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from "@mui/material"
import BaseWrapper from "../../components/BaseWrapper/BaseWrapper"
import { useCallback, useEffect, useState } from "react"
import { HistoryToggleOff } from "@mui/icons-material"
import { useParams } from "react-router-dom"
import { API } from "../../lib/api"
import LogsModal from "./LogsModal"
import Feedback from "../../components/Feedback/Feedback"
import { useThemePreference } from "../../contexts/ThemeContext"
import { getForecastMarginProductGroups, useAppSettings } from "../../contexts/AppSettingsContext"

const SingleProduct = () => {
    const { isDarkMode } = useThemePreference()
    const [loading,setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [logModal, setLogModal] = useState(false)
    const [productData, setProductData] = useState({})
    const [formData, setFormData] = useState({
        cost: "",
        price: "",
        manufacturerPartNumber: "",
        productCode: "",
        description: "",
        active: true,
    })
    const [logs, setLogs] = useState([])
    const [feedback, setFeedback] = useState({ open: false, message: "", type: "" })

    const { pid } = useParams()
    const { applicationSettings } = useAppSettings()
    const canEditProduct = true
    const productCodeOptions = getForecastMarginProductGroups(applicationSettings)

    const handleClose = () => setLogModal(false)

    const syncProductState = useCallback((response, productLogs) => {
        setProductData(response)
        setFormData({
            cost: response?.cost ?? "",
            price: response?.price ?? "",
            manufacturerPartNumber: response?.manufacturerPartNumber ?? "",
            productCode: response?.productCode ?? "",
            description: response?.description ?? "",
            active: response?.active !== false,
        })
        if (productLogs !== undefined) setLogs(productLogs)
    }, [])

    const handleFieldChange = (event) => {
        const { name, value } = event.target
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }))
    }

    const hasChanges =
        String(formData.cost ?? "") !== String(productData?.cost ?? "") ||
        String(formData.price ?? "") !== String(productData?.price ?? "") ||
        String(formData.productCode ?? "") !== String(productData?.productCode ?? "") ||
        String(formData.description ?? "") !== String(productData?.description ?? "") ||
        Boolean(formData.active) !== (productData?.active !== false)

    const handleSave = async () => {

        if (!hasChanges) {
            setFeedback({ open: true, message: "No product changes to save.", type: "info" })
            return
        }

        setSaving(true)

        const payload = [{
            _id: pid,
            data: {
                cost: formData.cost === "" ? 0 : Number(formData.cost),
                price: formData.price === "" ? 0 : Number(formData.price),
                productCode: formData.productCode,
                description: formData.description,
                active: Boolean(formData.active),
                lastModifiedBy: localStorage.name || productData?.lastModifiedBy,
            }
        }]

        const response = await API.Products.UpdateMulti(payload)

        if (response.status === 200) {
            const [updatedProduct, productLogs] = await Promise.all([
                API.Products.FetchOne(pid),
                API.Products.FetchLogs(pid),
            ])

            syncProductState(updatedProduct, productLogs)
            setFeedback({
                open: true,
                message: response?.data?.message || "Product updated successfully.",
                type: "success",
            })
        } else {
            setFeedback({
                open: true,
                message: response?.data?.message || "Failed to update product.",
                type: "error",
            })
        }

        setSaving(false)
    }

    useEffect(() => {
        let cancelled = false;
        async function fetchData() {
            const response = await API.Products.FetchOne(pid)
            const productLogs = await API.Products.FetchLogs(pid)
            if (cancelled) return;
            syncProductState(response, productLogs)
            setLoading(false)
        }
        fetchData()
        return () => { cancelled = true; };
    },[pid, syncProductState])

    return (
        <BaseWrapper
            content={
                loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                <>
                    <Feedback props={feedback} setFeedback={setFeedback} />
                    <div className="row">
                        <LogsModal logs={logs} open={logModal} handleClose={handleClose} />

                        <div className="col-xl-12 col-md-12" style={{ marginTop: "1em", marginBottom: "2em" }}>
                            <Paper className="hive-single-product-card" elevation={2} sx={{ maxWidth: 920, margin: "0 auto", padding: 4 }}>
                                <Stack spacing={3}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                                        <Box>
                                            <Typography variant="h5">
                                                Product Details
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Update pricing and description details for this product.
                                            </Typography>
                                        </Box>

                                        <Button variant="outlined" onClick={() => setLogModal(true)}>
                                            <HistoryToggleOff />
                                            History
                                        </Button>
                                    </Box>

                                    <Stack spacing={2} sx={{ maxWidth: 420 }}>
                                        <TextField
                                            label="Cost"
                                            name="cost"
                                            type="number"
                                            value={formData.cost}
                                            onChange={handleFieldChange}
                                            disabled={!canEditProduct}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Price"
                                            name="price"
                                            type="number"
                                            value={formData.price}
                                            onChange={handleFieldChange}
                                            disabled={!canEditProduct}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Manufacturer Part Number"
                                            name="manufacturerPartNumber"
                                            value={formData.manufacturerPartNumber}
                                            fullWidth
                                            disabled
                                        />
                                        <Autocomplete
                                            options={productCodeOptions}
                                            value={formData.productCode || null}
                                            onChange={(event, newValue) =>
                                                setFormData((prev) => ({ ...prev, productCode: newValue || "" }))
                                            }
                                            disabled={!canEditProduct}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Product Code"
                                                    helperText={"Use the forecast product-group label this quote section should resolve to."}
                                                />
                                            )}
                                            fullWidth
                                        />
                                    </Stack>

                                    <TextField
                                        label="Description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleFieldChange}
                                        fullWidth
                                        multiline
                                        rows={12}
                                        disabled={!canEditProduct}
                                        helperText={!canEditProduct ? "View only. Only engineering can edit this product." : ""}
                                    />

                                    <Box>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(formData.active)}
                                                    onChange={(event) =>
                                                        setFormData((prev) => ({ ...prev, active: event.target.checked }))
                                                    }
                                                    disabled={!canEditProduct}
                                                />
                                            }
                                            label={formData.active ? "Active product" : "Inactive product"}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            Inactive products remain on historical quotes but are hidden from quote item selection.
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Button variant={isDarkMode ? "outlined" : "contained"} onClick={handleSave} disabled={saving || !canEditProduct}>
                                            {saving ? "Saving..." : "Save"}
                                        </Button>
                                    </Box>
                                </Stack>
                            </Paper>
                        </div>
                    </div>
                </>
            )
        }/>
    )
}

export default SingleProduct
